import { current, isDraft, type Draft } from "immer";
import type { ReportTemplate, Node, PageSettings, DocumentStyles, TextStyle, Condition, FlowOptions, LibraryItem } from "../types/template.js";
import type { BlockStyle } from "../types/style.js";
import { getRegion, indexTemplate, listAt, type NodeLocation, type RegionId } from "./tree.js";
import { cloneWithNewIds, createColumn } from "../schema/create.js";
import { createId } from "../schema/ids.js";

/**
 * Commands are plain data. Each is applied by a reducer that mutates an immer
 * draft; immer turns the mutation into immutable structural sharing + patches.
 */
export type Command =
  | { type: "insertNode"; node: Node; at: NodeLocation }
  | { type: "insertNodes"; nodes: Node[]; at: NodeLocation }
  | { type: "removeNodes"; ids: string[] }
  | { type: "moveNode"; id: string; to: NodeLocation }
  | { type: "duplicateNode"; id: string; newId?: string }
  | { type: "setProps"; id: string; patch: Record<string, unknown> }
  | { type: "setProp"; id: string; path: string; value: unknown }
  | { type: "setStyle"; id: string; patch: BlockStyle | null }
  | { type: "setFlow"; id: string; patch: FlowOptions }
  | { type: "setVisibleWhen"; id: string; condition: Condition | undefined }
  | { type: "setName"; id: string; name: string | undefined }
  | { type: "setLocked"; id: string; locked: boolean }
  | { type: "setRowLayout"; id: string; layout: number[] }
  | { type: "wrapNodes"; ids: string[]; wrapper: Node }
  | { type: "replaceNode"; id: string; node: Node }
  | { type: "setPage"; patch: Partial<PageSettings> }
  | { type: "setDocumentStyles"; patch: Partial<DocumentStyles> }
  | { type: "setTextStyle"; id: string; patch: TextStyle | null }
  | { type: "setTemplateName"; name: string }
  | { type: "setSettings"; patch: NonNullable<ReportTemplate["settings"]> }
  | { type: "setRegionFirstPage"; region: "header" | "footer"; enabled: boolean }
  | { type: "addLibraryItem"; item: LibraryItem }
  | { type: "removeLibraryItem"; id: string }
  | { type: "replaceTemplate"; template: ReportTemplate };

export class CommandError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CommandError";
  }
}

function setDeep(obj: Record<string, unknown>, path: string, value: unknown) {
  const parts = path.split(".");
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i]!;
    const next = cur[p];
    if (Array.isArray(next)) cur = next as unknown as Record<string, unknown>;
    else if (next && typeof next === "object") cur = next as Record<string, unknown>;
    else {
      cur[p] = {};
      cur = cur[p] as Record<string, unknown>;
    }
  }
  const last = parts[parts.length - 1]!;
  if (value === undefined) delete cur[last];
  else cur[last] = value;
}

/** Applies a command to a template draft. Throws `CommandError` for invalid operations. */
export function applyCommand(draft: Draft<ReportTemplate>, cmd: Command): void {
  const t = draft as ReportTemplate;
  switch (cmd.type) {
    case "insertNode": {
      const { list, index } = listAt(t, cmd.at);
      list.splice(Math.min(index, list.length), 0, cmd.node);
      return;
    }
    case "insertNodes": {
      const { list, index } = listAt(t, cmd.at);
      list.splice(Math.min(index, list.length), 0, ...cmd.nodes);
      return;
    }
    case "removeNodes": {
      const index = indexTemplate(t);
      // Remove deepest-first so parent removals do not invalidate indices.
      const entries = cmd.ids.map((id) => index.get(id)).filter((e): e is NonNullable<typeof e> => !!e);
      entries.sort((a, b) => b.depth - a.depth || b.index - a.index);
      for (const e of entries) {
        const list = e.parent ? (e.parent.children ??= []) : getRegion(t, e.region);
        const i = list.findIndex((n) => n.id === e.node.id);
        if (i >= 0) list.splice(i, 1);
      }
      return;
    }
    case "moveNode": {
      const index = indexTemplate(t);
      const entry = index.get(cmd.id);
      if (!entry) throw new CommandError(`Node ${cmd.id} not found`);
      // Refuse to move a node into its own subtree.
      const target = listAt(t, cmd.to);
      let probe: Node | undefined = target.list === entry.node.children ? entry.node : undefined;
      if (probe) throw new CommandError("Cannot move a node inside itself");
      const containerPath = cmd.to.path.slice(0, -1);
      let cur = getRegion(t, cmd.to.region);
      for (const i of containerPath) {
        const n: Node | undefined = cur[i];
        if (!n) break;
        if (n.id === cmd.id) throw new CommandError("Cannot move a node inside itself");
        cur = n.children ?? [];
      }
      void probe;
      const sourceList = entry.parent ? (entry.parent.children ??= []) : getRegion(t, entry.region);
      const sourceIndex = sourceList.indexOf(entry.node);
      const [node] = sourceList.splice(sourceIndex, 1);
      const dest = listAt(t, cmd.to);
      let destIndex = dest.index;
      if (dest.list === sourceList && sourceIndex < destIndex) destIndex--;
      dest.list.splice(Math.min(destIndex, dest.list.length), 0, node!);
      return;
    }
    case "duplicateNode": {
      const index = indexTemplate(t);
      const entry = index.get(cmd.id);
      if (!entry) throw new CommandError(`Node ${cmd.id} not found`);
      const list = entry.parent ? (entry.parent.children ??= []) : getRegion(t, entry.region);
      const copy = cloneWithNewIds(isDraft(entry.node) ? current(entry.node) : entry.node);
      if (cmd.newId) copy.id = cmd.newId;
      list.splice(entry.index + 1, 0, copy);
      return;
    }
    case "setProps": {
      const n = requireNode(t, cmd.id);
      for (const [k, v] of Object.entries(cmd.patch)) {
        if (v === undefined) delete n.props[k];
        else n.props[k] = v;
      }
      return;
    }
    case "setProp": {
      const n = requireNode(t, cmd.id);
      setDeep(n.props, cmd.path, cmd.value);
      return;
    }
    case "setStyle": {
      const n = requireNode(t, cmd.id);
      if (cmd.patch === null) {
        delete n.style;
        return;
      }
      n.style ??= {};
      mergeStyle(n.style, cmd.patch);
      if (Object.keys(n.style).length === 0) delete n.style;
      return;
    }
    case "setFlow": {
      const n = requireNode(t, cmd.id);
      n.flow = { ...(n.flow ?? {}), ...cmd.patch };
      for (const k of Object.keys(n.flow) as Array<keyof FlowOptions>) if (n.flow[k] === undefined) delete n.flow[k];
      if (Object.keys(n.flow).length === 0) delete n.flow;
      return;
    }
    case "setVisibleWhen": {
      const n = requireNode(t, cmd.id);
      if (cmd.condition === undefined || cmd.condition === "") delete n.visibleWhen;
      else n.visibleWhen = cmd.condition;
      return;
    }
    case "setName": {
      const n = requireNode(t, cmd.id);
      if (cmd.name) n.name = cmd.name;
      else delete n.name;
      return;
    }
    case "setLocked": {
      const n = requireNode(t, cmd.id);
      if (cmd.locked) n.locked = true;
      else delete n.locked;
      return;
    }
    case "setRowLayout": {
      const row = requireNode(t, cmd.id);
      if (row.type !== "row") throw new CommandError("setRowLayout requires a row");
      const total = cmd.layout.reduce((a, b) => a + b, 0);
      if (total !== 12) throw new CommandError("Row layout must sum to 12");
      row.children ??= [];
      const columns = row.children;
      // Shrink: move orphaned children into the last surviving column.
      while (columns.length > cmd.layout.length) {
        const removed = columns.pop()!;
        const target = columns[columns.length - 1]!;
        target.children ??= [];
        target.children.push(...(removed.children ?? []));
      }
      while (columns.length < cmd.layout.length) columns.push(createColumn(cmd.layout[columns.length]!));
      columns.forEach((c, i) => {
        c.props.span = cmd.layout[i]!;
      });
      row.props.layout = [...cmd.layout];
      return;
    }
    case "wrapNodes": {
      const index = indexTemplate(t);
      const entries = cmd.ids.map((id) => index.get(id)).filter((e): e is NonNullable<typeof e> => !!e);
      if (entries.length === 0) return;
      const first = entries[0]!;
      const list = first.parent ? (first.parent.children ??= []) : getRegion(t, first.region);
      const nodes = entries.map((e) => e.node);
      const startIndex = list.indexOf(first.node);
      for (const n of nodes) {
        const i = list.indexOf(n);
        if (i >= 0) list.splice(i, 1);
      }
      const wrapper = { ...cmd.wrapper, children: [...(cmd.wrapper.children ?? []), ...nodes.map((n) => (isDraft(n) ? current(n) : n))] };
      list.splice(Math.min(startIndex, list.length), 0, wrapper);
      return;
    }
    case "replaceNode": {
      const index = indexTemplate(t);
      const entry = index.get(cmd.id);
      if (!entry) throw new CommandError(`Node ${cmd.id} not found`);
      const list = entry.parent ? (entry.parent.children ??= []) : getRegion(t, entry.region);
      list[entry.index] = cmd.node;
      return;
    }
    case "setPage": {
      Object.assign(t.page, structuredClone(cmd.patch));
      return;
    }
    case "setDocumentStyles": {
      const p = cmd.patch;
      if (p.text) for (const [id, s] of Object.entries(p.text)) t.styles.text[id] = { ...(t.styles.text[id] ?? {}), ...s };
      if (p.table) Object.assign(t.styles.table, p.table);
      if (p.card) Object.assign(t.styles.card, p.card);
      if (p.link) Object.assign(t.styles.link, p.link);
      if (p.spacing) Object.assign(t.styles.spacing, p.spacing);
      return;
    }
    case "setTextStyle": {
      if (cmd.patch === null) {
        delete t.styles.text[cmd.id];
        return;
      }
      const existing = t.styles.text[cmd.id] ?? {};
      for (const [k, v] of Object.entries(cmd.patch)) {
        if (v === undefined) delete (existing as Record<string, unknown>)[k];
        else (existing as Record<string, unknown>)[k] = v;
      }
      t.styles.text[cmd.id] = existing;
      return;
    }
    case "setTemplateName": {
      t.name = cmd.name;
      return;
    }
    case "setSettings": {
      t.settings = { ...(t.settings ?? {}), ...cmd.patch };
      return;
    }
    case "setRegionFirstPage": {
      t.page[cmd.region].differentFirstPage = cmd.enabled;
      if (cmd.enabled && !t[cmd.region].firstPage) t[cmd.region].firstPage = (isDraft(t[cmd.region].default) ? current(t[cmd.region].default) : t[cmd.region].default).map(cloneWithNewIds);
      return;
    }
    case "addLibraryItem": {
      t.library ??= [];
      t.library.push({ ...cmd.item, id: cmd.item.id || createId("lib") });
      return;
    }
    case "removeLibraryItem": {
      t.library = (t.library ?? []).filter((l) => l.id !== cmd.id);
      return;
    }
    case "replaceTemplate": {
      const fresh = structuredClone(cmd.template);
      for (const k of Object.keys(t) as Array<keyof ReportTemplate>) delete (t as unknown as Record<string, unknown>)[k];
      Object.assign(t, fresh);
      return;
    }
  }
}

function requireNode(t: ReportTemplate, id: string): Node {
  const e = indexTemplate(t).get(id);
  if (!e) throw new CommandError(`Node ${id} not found`);
  return e.node;
}

function mergeStyle(target: BlockStyle, patch: BlockStyle) {
  for (const [k, v] of Object.entries(patch) as Array<[keyof BlockStyle, unknown]>) {
    if (v === undefined || v === null) {
      delete target[k];
      continue;
    }
    if ((k === "padding" || k === "margin" || k === "border") && typeof v === "object") {
      const merged = { ...((target[k] as Record<string, unknown>) ?? {}), ...(v as Record<string, unknown>) };
      for (const key of Object.keys(merged)) if (merged[key] === undefined) delete merged[key];
      if (Object.keys(merged).length === 0) delete target[k];
      else (target as Record<string, unknown>)[k] = merged;
      continue;
    }
    (target as Record<string, unknown>)[k] = v;
  }
}

export type { NodeLocation, RegionId };
